import React, { useCallback, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import isNil from 'lodash/isNil';
import styled from 'styled-components';
import { AccordionSection, IgnoredChecksBreakdown, SplitChecksToggleBreakdown, Tag, Text, ToggleableBreakdown } from 'components';
import { ParticipantListItem } from 'components/Calculator/ParticipantListItem';
import { theme, toRem } from 'design';
import { ICalculatorRules, IPaymentSetCalculations, IPayout, ITipCalculatorResultWarning, ITipCalculatorResultWarningUnallocatedRemainder, TipCalculatorMethod, TipCalculatorWarningType } from 'types';
import { buildEmployeeOverrideTicketBreakdown, buildTipCalculatorParticipants, buildTipCalculatorWarnings } from 'utils';
import { sortAlphaNumerically } from 'utils/arrays';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay } from 'utils/currency';
import { toTitleCase } from 'utils/strings';
import { IExportData } from './types';

interface ICalculationsReportProps {
  header: (data?: IExportData, capWarnings?: ITipCalculatorResultWarning[], unallocatedFundsWarnings?: ITipCalculatorResultWarningUnallocatedRemainder[]) => React.ReactNode;
  calculations: IPaymentSetCalculations;
  payouts: IPayout[];
  isLoading?: boolean;
}

const StyledAccordionSection = styled(AccordionSection)`
  /* AccordionSection header content */
  .payee-calculations-breakdown-header {
    border-bottom: unset;

    section {
      width: auto;

      &:first-child {
        flex-basis: ${toRem(100)};

        h3 {
          font-size: ${toRem(16)};
        }

        .mobile-tag {
          display: none;
        }
      }

      &:last-child {
        flex-grow: 1;

        .desktop-tag {
          display: flex;
        }
      }
    }

    @media (max-width: ${theme.breakpoint.large}px) {
      padding: ${toRem(8)} 0;

      > :first-child {
        flex-flow: column nowrap;
        align-items: flex-start;
      }

      section {
        width: 100%;

        &:first-child {
          flex-basis: 0;

          div {
            flex-flow: row nowrap;
            align-items: center;
            justify-content: space-between;
            width: 100%;

            h3 {
              max-width: calc(100% - ${toRem(50)});
              font-size: ${toRem(14)};
            }

            .mobile-tag {
              display: flex;
            }
          }
        }

        &:last-child {
          .desktop-tag {
            display: none;
          }
        }
      }
    }
  }

  /* AccordionSection expanded content */
  .payee-calculations-breakdown-content {
    background: ${theme.color.neutral[50]};
    padding: 0 ${toRem(16)};
  }
`;

const TotalsGrid = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .grid-label {
    color: ${theme.color.neutral[500]};
  }

  .grid-value {
    font-size: ${toRem(14)};
  }
`;

const TotalWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: ${toRem(100)};
  margin-right: ${toRem(16)};
`;

const StyledToggleableBreakdown = styled(ToggleableBreakdown)`
  margin-bottom: ${toRem(16)};
`;

const NoteSection = styled.div`
  width: 100%;
  border-top: 1px solid ${theme.color.neutral[200]};
  padding: ${toRem(16)} 0;
  margin-top: ${toRem(16)};
`;

const Note = styled.div`
  background-color: ${theme.color.white};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${toRem(theme.borderRadius.xsmall)};
  font-size: ${toRem(14)};
  padding: ${toRem(13)} ${toRem(11)};
  width: 100%;
`;

const OverrideChecksWrapper = styled.div`
  &:not(:first-child) {
    margin-bottom: ${toRem(16)};
  }
  width: 100%;
`;

const StyledParticipantListItem = styled.div`
  width: 100%;

  .participant-info section p {
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.semiBold};
    color: ${theme.color.neutral[900]};
  }

  > div {
    margin-bottom: ${toRem(22)};
    padding-bottom: ${toRem(8)};
  }

  .m-totals-grid {
    margin-top: 0;

    div > div {
      margin-bottom: 0;
    }
  }

  .top-row {
    padding-top: ${toRem(8)};

    > div {
      justify-content: flex-end;
      flex-basis: initial;
    }

    .dt-totals-grid {
      div > div {
        margin-bottom: 0;
      }
    }
  }

  @media (min-width: ${theme.breakpoint.large}px) {
    > div {
      padding-bottom: ${toRem(16)};
    }

    .participant-info section p {
      font-size: ${toRem(16)};
    }

    .top-row {
      padding: ${toRem(16)} ${toRem(22)} 0 ${toRem(22)};

      .dt-totals-grid {
        display: grid;
        margin-top: 0;
      }

      button > div {
        margin-left: 0;
      }
    }

    .m-totals-grid {
      display: none;
    }
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    .top-row {
      > div {
        flex-basis: ${toRem(452.8)};
      }
    }
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    .top-row {
      > button {
        flex-grow: 1;
        max-width: none;
        width: auto;
      }

      > div {
        flex-shrink: 0;
        flex-grow: 1;

        > div {
          padding: 0;
        }
      }
    }
  }
`;

const CalculationsReport = ({ header, calculations, payouts, isLoading }: ICalculationsReportProps) => {
  // Get Tip Calculator Warnings
  const tipCalculatorWarnings = useMemo(() => buildTipCalculatorWarnings(calculations.results || []), [calculations]);

  // Filter for cap warnings
  const cappedWarnings = useMemo(() => tipCalculatorWarnings.filter((warning) => [TipCalculatorWarningType.CAPPED_REMAINDER, TipCalculatorWarningType.CAPPED_SHARE].includes(warning.type)), [tipCalculatorWarnings]);

  // Filter for unallocated funds warnings
  const unallocatedFundsWarnings = useMemo(
    () => tipCalculatorWarnings.filter((warning) => [TipCalculatorWarningType.UNALLOCATED_REMAINDER].includes(warning.type)) as ITipCalculatorResultWarningUnallocatedRemainder[],
    [tipCalculatorWarnings]
  );

  const renderTotalsGrid = (payout: IPayout) => (
    <TotalsGrid data-cy="payee-totals">
      <TotalWrapper data-cy="payee-calculated-total">
        <Text className="grid-label" fontWeight="medium" size="xxsmall">
          Calculated Tips
        </Text>
        <Text className="grid-value" fontWeight="medium">
          {centsToDollarsDisplay(payout.gross)}
        </Text>
      </TotalWrapper>
      {!isNil(payout.holdback) && (
        <TotalWrapper data-cy="payee-holdback-total">
          <Text className="grid-label" fontWeight="medium" size="xxsmall">
            Holdback
          </Text>
          <Text className="grid-value" fontWeight="medium">
            {centsToNegativeDollarsDisplay(payout.holdback)}
          </Text>
        </TotalWrapper>
      )}
      <TotalWrapper data-cy="payee-net">
        <Text className="grid-label" fontWeight="medium" size="xxsmall">
          Net
        </Text>
        <Text className="grid-value" fontWeight="medium">
          {centsToDollarsDisplay(payout.net)}
        </Text>
      </TotalWrapper>
      <Tag data-cy="payee-disbursement-method" className="desktop-tag" label={toTitleCase(payout.disbursementMethod)} appearance={payout.disbursementMethod} />
    </TotalsGrid>
  );

  const renderBreakdownForPayee = useCallback(
    (payeeId: string) =>
      calculations?.results.map((segment) => {
        const employeePayouts = segment.employees.filter((employee) => employee.id === payeeId && (employee.received.length || employee.contributions.length || employee.retained?.total > 0));
        if (!payeeId || !employeePayouts?.length) return <></>;

        return employeePayouts
          .filter((r) => r.calculated.total > 0 || r.totals.totalMinWorked > 0)
          .map((employeePayout, idx) => (
            <StyledToggleableBreakdown
              key={`segment-breakdown-${payeeId}-${idx}`}
              categoryGroups={segment.meta.categoryGroups}
              calculatorRules={segment.rules}
              calculation={employeePayout}
              segmentName={segment.segment}
              creditCardFee={segment.creditCardPercentFee}
            />
          ));
      }),
    [calculations]
  );

  const renderOverrideBreakdown = useCallback(
    (payeeId: string) => {
      const employeePayouts = calculations?.results.flatMap((result) => result.employees.filter((employee) => employee.id === payeeId));
      const { splitChecks, ignoredChecks } = buildEmployeeOverrideTicketBreakdown(employeePayouts);

      return (
        <>
          {(!!splitChecks.received?.length || !!splitChecks.contributions?.length) && (
            <OverrideChecksWrapper>
              <SplitChecksToggleBreakdown splitChecks={splitChecks} />
            </OverrideChecksWrapper>
          )}
          {!!ignoredChecks.length && (
            <OverrideChecksWrapper>
              <IgnoredChecksBreakdown ignoredCheckData={ignoredChecks.sort(sortAlphaNumerically('displayId'))} />
            </OverrideChecksWrapper>
          )}
        </>
      );
    },
    [calculations]
  );

  // Supports legacy points/hours payment-sets in development env
  if (!isLoading && calculations?.method !== TipCalculatorMethod.SHARE) {
    return (
      <>
        {payouts &&
          payouts.map((payout) => <StyledAccordionSection key={`payee-${payout.employee.id}`} className="payee-calculations-breakdown" title={payout.employee.name} actions={renderTotalsGrid(payout)} open={false} compact hideButton />)}
      </>
    );
  }

  const renderParticipants = () => {
    if (isLoading || !calculations?.results[0].participants?.length) {
      return <></>;
    }

    const participants = buildTipCalculatorParticipants(calculations.results);

    return participants.map((participant, index) => (
      <StyledParticipantListItem key={`participant-${index}`}>
        <ParticipantListItem participant={participant} calculatorRules={{ categoryGroups: calculations.results[index].meta.categoryGroups, rules: calculations.results[index].rules } as ICalculatorRules} />
      </StyledParticipantListItem>
    ));
  };

  return (
    <>
      {header(undefined, cappedWarnings, unallocatedFundsWarnings)}
      {isLoading || !payouts?.length ? (
        <StyledAccordionSection title={<Skeleton width={100} />} actions={<Skeleton width={50} count={4} style={{ marginRight: toRem(16) }} />} open={false} compact />
      ) : (
        <>
          {renderParticipants()}
          {payouts.map((payout) => (
            <StyledAccordionSection
              key={`payee-${payout.employee.id}`}
              className="payee-calculations-breakdown"
              dataCy="payee-calculations-breakdown"
              title={payout.employee.name}
              subtitle={<Tag data-cy="payee-disbursement-method" className="mobile-tag" label={toTitleCase(payout.disbursementMethod)} appearance={payout.disbursementMethod} />}
              actions={renderTotalsGrid(payout)}
              open={false}
              compact
            >
              {renderBreakdownForPayee(payout.employee.id)}
              {renderOverrideBreakdown(payout.employee.id)}
              {payout.notes && (
                <NoteSection>
                  <Note>{payout.notes}</Note>
                </NoteSection>
              )}
            </StyledAccordionSection>
          ))}
        </>
      )}
    </>
  );
};

export default CalculationsReport;
